/**
 * MockStore: the in-memory backing store the mock runtime uses for every
 * capability. Tables keyed by id; lookups by id are O(1); list scans are linear
 * but the dataset stays small (fixtures < 5k rows).
 *
 * `withShadow` clones the store into a transactional copy, runs a handler, and
 * either commits the change or discards it. This is how dry-run works without
 * mutating visible state.
 *
 * See docs/01-foundations/mock-runtime.md.
 */

import { HostingPortalEventBus } from './bus'

/**
 * Tables the mock runtime knows about — the in-memory analogue of the Aurora
 * tables PLAN-01 defines in DDL. This union must stay in agreement with the
 * canonical CRM domain model (MASTER-PLAN §4). PLAN-00 owns this file; PLAN-01
 * adds a table by PR to this union (see PLAN-00 §5).
 *
 * Each module's seed factory contributes rows for its tables; the store creates
 * a new table lazily on `put` if the name appears here.
 */
export type TableName =
  // identity + tenancy
  | 'workspaces'
  | 'users'
  | 'members'
  | 'roles'
  | 'teams'
  | 'team_members'
  | 'provider_keys'
  // vertical-pack config (PLAN-10)
  | 'vertical_packs'
  | 'pack_object_configs'
  | 'pack_property_configs'
  | 'pack_view_configs'
  | 'pack_theme_overrides'
  | 'pack_lifecycle_configs'
  | 'pack_personalization_tokens'
  | 'workspace_credit_usage'
  | 'saved_views'
  // core CRM records
  | 'companies'
  | 'contacts'
  | 'addresses'
  | 'deals'
  | 'subscriptions'
  | 'quotes'
  | 'quote_line_items'
  | 'invoices'
  | 'products'
  // activity + tasks
  | 'activities'
  | 'notes'
  | 'tasks'
  // bookings (Calendly-style scheduling)
  | 'event_types'
  | 'availability_schedules'
  | 'bookings'
  // automation
  | 'workflows'
  // product adoption + hosting services (live custom objects + archived analogues)
  | 'product_adoptions'
  | 'hosting_services'
  | 'upmind_product_metrics'
  | 'upmind_services'
  // enrichment + analytics rollups
  | 'company_product_rollups'
  | 'product_share_rollups'
  | 'install_volume_tiers'
  | 'zoominfo_enrichments'
  | 'mixpanel_engagement'
  | 'analytics_events'
  | 'engagement_events'
  | 'communication_preferences'
  | 'company_aliases'
  // rollup freshness watermark + AI-cleanup review queue (PLAN-05; DR-15 additive edit)
  | 'rollup_runs'
  | 'enrichment_cleanup_batches'
  // integrations framework (PLAN-12; DR-14 additive edit)
  | 'integration_connections'
  | 'integration_runs'
  | 'mailchimp_subscribers'
  | 'upmind_match_review'
  | 'rb2b_signals'
  | 'raw_zoominfo'
  | 'raw_upmind_services'
  | 'raw_upmind_product_metrics'
  | 'raw_mixpanel_events'
  | 'raw_mailchimp'
  | 'raw_quickbooks'
  // outreach
  | 'sequences'
  | 'sequence_steps'
  | 'sequence_enrollments'
  | 'email_sends'
  | 'email_events'
  // e-sign
  | 'esign_documents'
  | 'esign_signers'
  | 'esign_events'
  // platform: trust + config-VCS + audit
  | 'audit_log'
  | 'config_commits'
  | 'config_branches'
  | 'config_tags'
  | 'interest_tag_definitions'
  | 'interest_tag_rules'
  // reference / seed data
  | 'deal_pipeline_stages'
  // agent
  | 'agent_runs'
  | 'agent_skills'
  | 'rag_document'
  // analytics dashboards + saved views (PLAN-06; DR-16 additive edit)
  | 'analytics_dashboards'
  | 'analytics_saved_views'
  // HubSpot→hosting.com CRM migration (PLAN-09; DR-17 additive edit)
  | 'address_associations'
  | 'migration_id_map'
  | 'migration_checkpoints'
  | 'migration_batches'
  // generic record↔record associations (the labeled record_associations junction)
  | 'record_associations'

/** The canonical ordered list of every table the store pre-creates at boot. */
export const TABLE_NAMES = [
  'workspaces',
  'users',
  'members',
  'roles',
  'teams',
  'team_members',
  'provider_keys',
  'vertical_packs',
  'pack_object_configs',
  'pack_property_configs',
  'pack_view_configs',
  'pack_theme_overrides',
  'pack_lifecycle_configs',
  'pack_personalization_tokens',
  'workspace_credit_usage',
  'saved_views',
  'companies',
  'contacts',
  'addresses',
  'deals',
  'subscriptions',
  'quotes',
  'quote_line_items',
  'invoices',
  'products',
  'activities',
  'notes',
  'tasks',
  'event_types',
  'availability_schedules',
  'bookings',
  'workflows',
  'product_adoptions',
  'hosting_services',
  'upmind_product_metrics',
  'upmind_services',
  'company_product_rollups',
  'product_share_rollups',
  'install_volume_tiers',
  'zoominfo_enrichments',
  'mixpanel_engagement',
  'analytics_events',
  'engagement_events',
  'communication_preferences',
  'company_aliases',
  'rollup_runs',
  'enrichment_cleanup_batches',
  'integration_connections',
  'integration_runs',
  'mailchimp_subscribers',
  'upmind_match_review',
  'rb2b_signals',
  'raw_zoominfo',
  'raw_upmind_services',
  'raw_upmind_product_metrics',
  'raw_mixpanel_events',
  'raw_mailchimp',
  'raw_quickbooks',
  'sequences',
  'sequence_steps',
  'sequence_enrollments',
  'email_sends',
  'email_events',
  'esign_documents',
  'esign_signers',
  'esign_events',
  'audit_log',
  'config_commits',
  'config_branches',
  'config_tags',
  'interest_tag_definitions',
  'interest_tag_rules',
  'deal_pipeline_stages',
  'agent_runs',
  'agent_skills',
  'rag_document',
  'analytics_dashboards',
  'analytics_saved_views',
  'address_associations',
  'migration_id_map',
  'migration_checkpoints',
  'migration_batches',
  'record_associations',
] as const satisfies readonly TableName[]

/**
 * A normalised write op. `put` and `patch` both collapse to `set` (upsert of
 * the resulting row); `delete` becomes `del`. Cross-tab deltas speak this shape.
 */
export type WriteOp = 'set' | 'del'

/**
 * Optional sink attached to a MockStore. Receives every *local* write (put /
 * patch / delete) so a transport can persist it and broadcast it to other tabs.
 * Kept deliberately tiny: the store knows nothing about BroadcastChannel,
 * IndexedDB, or localStorage — all of that lives behind this one hook.
 */
export interface MockStoreTransport {
  /** Called after a local write has been applied to the in-memory store. */
  onWrite(table: TableName, id: string, op: WriteOp, row: unknown | undefined): void
  /** Flush anything pending and tear down listeners. */
  dispose(): void
}

export class MockStore {
  /** EventTarget that powers fake SSE in mock mode. */
  readonly bus = new HostingPortalEventBus()

  /** Per-table maps. New tables are added when fixtures seed them. */
  private tables = new Map<TableName, Map<string, unknown>>()

  /** Optional cross-tab transport. Attached by `bootMockRuntime` in the browser. */
  private transport?: MockStoreTransport

  constructor(seed?: Partial<Record<TableName, unknown[]>>) {
    for (const table of TABLE_NAMES) {
      this.tables.set(table, new Map())
    }
    if (seed) {
      for (const [name, rows] of Object.entries(seed) as [TableName, unknown[] | undefined][]) {
        if (!rows) continue
        const map = this.tables.get(name) ?? new Map()
        for (const row of rows) {
          const id = (row as { id?: string }).id
          if (typeof id !== 'string') {
            throw new Error(`Seed row for "${name}" is missing an id field.`)
          }
          map.set(id, row)
        }
        this.tables.set(name, map)
      }
    }
  }

  /** Return all rows in a table (caller should not mutate the returned array). */
  list<T = unknown>(table: TableName): T[] {
    const map = this.tables.get(table)
    if (!map) return []
    return Array.from(map.values()) as T[]
  }

  get<T = unknown>(table: TableName, id: string): T | undefined {
    return this.tables.get(table)?.get(id) as T | undefined
  }

  put<T extends { id: string }>(table: TableName, row: T): T {
    const map = this.tables.get(table) ?? new Map()
    map.set(row.id, row)
    this.tables.set(table, map)
    this.transport?.onWrite(table, row.id, 'set', row)
    return row
  }

  patch<T extends { id: string }>(table: TableName, id: string, fn: (row: T) => T): T | undefined {
    const map = this.tables.get(table)
    if (!map) return undefined
    const current = map.get(id) as T | undefined
    if (!current) return undefined
    const next = fn(current)
    map.set(id, next)
    this.transport?.onWrite(table, id, 'set', next)
    return next
  }

  delete(table: TableName, id: string): boolean {
    const deleted = this.tables.get(table)?.delete(id) ?? false
    if (deleted) this.transport?.onWrite(table, id, 'del', undefined)
    return deleted
  }

  /** Attach a cross-tab transport. Idempotent-ish: replaces any prior one. */
  attachTransport(transport: MockStoreTransport): void {
    this.transport = transport
  }

  /**
   * Apply a delta that originated in *another* tab (or a persisted overlay at
   * boot). Mutates the in-memory tables directly and — crucially — does NOT
   * call the transport, so it never re-broadcasts or re-persists. The caller
   * (transport / boot) is responsible for any bus re-emit.
   */
  applyRemote(table: TableName, id: string, op: WriteOp, row: unknown | undefined): void {
    const map = this.tables.get(table) ?? new Map()
    if (op === 'del') map.delete(id)
    else map.set(id, row)
    this.tables.set(table, map)
  }

  /**
   * Run a handler against a deep-cloned shadow of every table. The handler's
   * mutations are visible to it, then thrown away. Used for dry-run + bulk
   * preview.
   */
  async withShadow<T>(handler: () => Promise<T>): Promise<T> {
    const snapshot = new Map<TableName, Map<string, unknown>>()
    for (const [name, map] of this.tables) {
      snapshot.set(name, new Map(map))
    }
    try {
      // Replace tables with deep clones.
      for (const [name, map] of this.tables) {
        const cloned = new Map<string, unknown>()
        for (const [k, v] of map) cloned.set(k, structuredClone(v))
        this.tables.set(name, cloned)
      }
      return await handler()
    } finally {
      // Restore.
      for (const [name, map] of snapshot) this.tables.set(name, map)
    }
  }

  /** Sort the table by (row[field] cmp) into a fresh array. */
  sortedList<T extends Record<string, unknown>>(
    table: TableName,
    field: string,
    direction: 'asc' | 'desc' = 'desc',
  ): T[] {
    const rows = this.list<T>(table)
    const sign = direction === 'asc' ? 1 : -1
    return rows.slice().sort((a, b) => {
      const av = a[field]
      const bv = b[field]
      if (av === bv) return 0
      if (av === undefined || av === null) return 1
      if (bv === undefined || bv === null) return -1
      return av < bv ? -1 * sign : 1 * sign
    })
  }

  /** Reset all tables. Test-only. */
  _reset(): void {
    for (const map of this.tables.values()) map.clear()
  }
}

let store: MockStore | null = null

export function installMockStore(s: MockStore): void {
  store = s
  if (typeof globalThis !== 'undefined') {
    ;(globalThis as { __HOSTINGPORTAL_MOCK_STORE__?: MockStore }).__HOSTINGPORTAL_MOCK_STORE__ = s
  }
}

export function getMockStore(): MockStore {
  if (!store) {
    const g = globalThis as { __HOSTINGPORTAL_MOCK_STORE__?: MockStore }
    if (g.__HOSTINGPORTAL_MOCK_STORE__) store = g.__HOSTINGPORTAL_MOCK_STORE__
    else {
      store = new MockStore()
      g.__HOSTINGPORTAL_MOCK_STORE__ = store
    }
  }
  return store
}
